export type Language = 'en' | 'uk';

export type SourceId =
  | 'linkedin'
  | 'github'
  | 'djinni'
  | 'dou'
  | 'workua'
  | 'robotaua'
  | 'telegram'
  | 'instagram'
  | 'pool';

export interface User {
  id: string;
  email: string;
  name: string;
  avatarUrl?: string;
  createdAt: string;
}

export interface SearchRecord {
  id: string;
  userId: string;
  title: string;
  criteria: SearchCriteria;
  status: SearchStatus;
  /** Per-source status map — updated in real-time during background search */
  sourceStatuses: Record<string, string>;
  /** Post-mortem diagnostics (P0-5): per-source counts + reasons, no PII. */
  diagnostics?: SearchDiagnostics;
  createdAt: string;
  updatedAt: string;
}

export interface SourceDiagnostic {
  sourceId: string;
  status: string;
  candidateCount: number;
  durationMs: number;
  reason?: string;
}

export interface SearchDiagnostics {
  totalRaw: number;
  deduped: number;
  perSource: SourceDiagnostic[];
}

/**
 * Search lifecycle:
 * - `pending`   created, gathering criteria in chat
 * - `ready`     criteria confirmed — waiting for the user to press "Run search"
 * - `running`   background fan-out in progress
 * - `completed` finished with results
 * - `failed`    runner errored
 */
export type SearchStatus = 'pending' | 'ready' | 'running' | 'completed' | 'failed';

/**
 * How an individual candidate record was acquired (provenance). Stored per
 * candidate so the UI/audit can always explain where a profile came from.
 */
export type AcquiredVia = 'github-api' | 'xray-assist' | 'candidate-optin' | 'licensed' | 'demo';

/**
 * Final, honest result state of a source after it resolves:
 * - `live`        real fetch succeeded with ≥1 parsed candidate
 * - `empty`       real fetch succeeded but found 0 candidates (honest no-match)
 * - `unavailable` fetch failed / non-2xx / source disabled
 * - `demo`        synthetic data (mock-only sources, or explicit demo mode)
 */
export type SourceResultStatus = 'live' | 'empty' | 'unavailable' | 'demo';

/**
 * Per-source progress state written by the background search runner and read by
 * the chat UI. `idle`/`searching` are transient; the rest are final
 * ({@link SourceResultStatus}).
 */
export type SourceState = 'idle' | 'searching' | SourceResultStatus;

export interface SearchCriteria {
  role: string;
  skills: string[];
  location?: string;
  experienceYears?: number;
  remote?: boolean;
  languages?: string[];
  salaryMin?: number;
  salaryMax?: number;
  additionalNotes?: string;
  /** Free-text "who we ARE looking at" — prioritized positively in ranking. */
  include?: string;
  /** Free-text "who we are NOT looking at" — penalized / flagged in ranking. */
  exclude?: string;
}

/**
 * Per-criterion verdict for a candidate against ONE search criterion:
 * - `match`    — the candidate's data directly confirms the criterion
 * - `mismatch` — the data directly contradicts it (candidate explicitly states otherwise)
 * - `unknown`  — not enough data / criterion not mentioned. NOT a bug; common for
 *   X-ray snippets. Never invent facts → absence of info is `unknown`, not `mismatch`.
 */
export type CriterionStatus = 'match' | 'mismatch' | 'unknown';

export interface CriteriaAssessmentItem {
  /** The criterion label, exactly as derived from the search criteria. */
  criterion: string;
  status: CriterionStatus;
  /** Short evidence fragment (≤160 chars) for match/mismatch; '' for unknown. */
  evidence: string;
}

export interface Candidate {
  id: string;
  searchId: string;
  name: string;
  title?: string;
  location?: string;
  profileUrl: string;
  sourceId: SourceId;
  score: number; // 0–100
  skills: string[];
  experience?: string;
  summary?: string;
  gaps?: string[];
  /** Per-criterion match/mismatch/unknown assessment (one item per criterion). */
  criteriaAssessment?: CriteriaAssessmentItem[];
  /** Provenance — how this candidate was obtained. */
  acquiredVia?: AcquiredVia;
  acquiredAt?: string;
  rankedAt?: string;
  createdAt: string;
}

export type CandidateStatus = 'pending' | 'reviewed' | 'contacted' | 'declined';

export interface FavoriteCandidate {
  id: string;
  userId: string;
  candidateId: string;
  candidate?: Candidate;
  status: CandidateStatus;
  notes?: string;
  createdAt: string;
}

export interface ChatMessage {
  id: string;
  searchId: string;
  role: 'user' | 'assistant';
  content: string;
  createdAt: string;
}

export interface ApiResponse<T> {
  data: T;
  error?: never;
}

export interface ApiError {
  data?: never;
  error: { message: string; code?: string };
}

export type ApiResult<T> = ApiResponse<T> | ApiError;

/**
 * Derive the ordered, deduped list of human-readable criterion labels from the
 * structured search criteria. This is the SAME list passed to the ranking LLM and
 * used to backfill missing assessments — so the UI always shows exactly these.
 * Capped to keep the prompt and the per-candidate badge row compact.
 */
export function criteriaChecklist(c: SearchCriteria): string[] {
  const list: string[] = [];
  if (c.role?.trim()) list.push(c.role.trim());
  for (const s of c.skills ?? []) if (s.trim()) list.push(s.trim());
  if (c.location?.trim()) list.push(`Location: ${c.location.trim()}`);
  if (typeof c.experienceYears === 'number') list.push(`Experience ${c.experienceYears}+ years`);
  if (typeof c.remote === 'boolean') list.push(c.remote ? 'Remote' : 'On-site');
  for (const l of c.languages ?? []) if (l.trim()) list.push(`Language: ${l.trim()}`);
  // NOTE: include/exclude are deliberately excluded — they are SEARCH FILTERS
  // ("who we ARE / are NOT looking at"), not per-candidate attributes, so a ✓/✗/—
  // verdict on them is semantically wrong. They are surfaced via searchFilters().

  const seen = new Set<string>();
  const out: string[] = [];
  for (const x of list) {
    const k = x.toLowerCase();
    if (!seen.has(k)) {
      seen.add(k);
      out.push(x);
    }
    if (out.length >= 12) break;
  }
  return out;
}

/**
 * The free-text search filters ("who we ARE / are NOT looking at"). These shape
 * the search, they are not per-candidate criteria — render them in their own block,
 * never as ✓/✗/— badges on a candidate.
 */
export function searchFilters(c: SearchCriteria): { include?: string; exclude?: string } {
  const f: { include?: string; exclude?: string } = {};
  if (c.include?.trim()) f.include = c.include.trim();
  if (c.exclude?.trim()) f.exclude = c.exclude.trim();
  return f;
}

/**
 * Normalise a raw LLM `criteriaAssessment` array against the authoritative
 * `criteriaList`: returns EXACTLY one item per criterion, in order, with a valid
 * status. Any criterion the model omitted (or returned malformed) becomes
 * `unknown` — never `mismatch` — so the UI always has the full, honest set.
 */
export function normalizeCriteriaAssessment(
  raw: unknown,
  criteriaList: string[],
): CriteriaAssessmentItem[] {
  const arr: Record<string, unknown>[] = Array.isArray(raw)
    ? raw.filter((it): it is Record<string, unknown> => !!it && typeof it === 'object')
    : [];

  const read = (o: Record<string, unknown> | undefined): { status: CriterionStatus; evidence: string } => {
    if (!o) return { status: 'unknown', evidence: '' };
    const s = o['status'];
    const status: CriterionStatus = s === 'match' || s === 'mismatch' ? s : 'unknown';
    // Evidence only makes sense for a concrete verdict; never for unknown.
    const evidence =
      status === 'unknown' ? '' : typeof o['evidence'] === 'string' ? (o['evidence'] as string).slice(0, 160) : '';
    return { status, evidence };
  };

  const byName = new Map<string, Record<string, unknown>>();
  for (const o of arr) {
    const criterion = typeof o['criterion'] === 'string' ? (o['criterion'] as string).trim().toLowerCase() : '';
    if (criterion) byName.set(criterion, o);
  }

  // Position fallback: when the model returned one entry per criterion in order
  // (but possibly paraphrased the label), align by index instead of dropping to unknown.
  const sameLength = arr.length === criteriaList.length;

  return criteriaList.map((criterion, idx) => {
    const match = byName.get(criterion.toLowerCase()) ?? (sameLength ? arr[idx] : undefined);
    return { criterion, ...read(match) };
  });
}
